import React, { useEffect, useRef, useState } from "react";

// A2-581 screen map lead 5: how many times a finished job's result is asked
// for before the run is reported failed, and what the operator is told.
const RESULT_TRIES = 3;
const RESULT_UNAVAILABLE = {
  detail: "The calculation finished, but its result could not be loaded. "
    + "Open it from History, or run it again.",
};
import { useTranslation } from "react-i18next";
import { X } from "@phosphor-icons/react";

/**
 * v0.4.39 T1 — Async calc progress card.
 *
 * Polls the calc-job status endpoint every 2s and renders:
 *   - phase label
 *   - "Rows classified: N of M" counter
 *   - "VIES checks: N pending" counter
 *   - Cancel button (appears after 10 seconds of elapsed time)
 *
 * Props:
 *   job             — {job_id, status, progress, calc_id, error}
 *   onCancel        — async () → void (calls /cancel)
 *   onComplete      — (calcId) => void (called on COMPLETE)
 *   elapsedMs       — number, time since start
 */
export const CalcProgressCard = ({ job, onCancel, elapsedMs }) => {
  const { t } = useTranslation();
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  if (!job) return null;
  const p = job.progress || {};
  const status = job.status;
  const rowsTotal = p.rows_total || 0;
  const rowsDone = p.rows_classified || 0;
  const viesTotal = p.vies_total || 0;
  const viesPending = p.vies_pending || 0;
  const phase = p.phase || "queued";
  // v0.4.40 B9 — cancel gate lowered from 10s to 3s. The async
  // pipeline now routinely completes 29k-row jobs in ~2s, so 10s
  // made the button unreachable in normal use. 3s gives the operator
  // a chance to react without dominating the spinner phase.
  const showCancel = elapsedMs >= 3_000 &&
    !["COMPLETE", "FAILED", "CANCELLED"].includes(status);

  const phaseLabel = {
    queued: t("ui.calculate.progress.phase_queued",
                "Queued — waiting to start"),
    queued_for_lock: t("ui.calculate.progress.phase_queued_for_lock",
                          "Waiting in queue"),
    starting: t("ui.calculate.progress.phase_starting", "Starting"),
    load: t("ui.calculate.progress.phase_load",
              "Loading transactions"),
    vies_prepass: t("ui.calculate.progress.phase_vies",
                      "Pre-fetching VIES VAT validations"),
    classify: t("ui.calculate.progress.phase_classify",
                  "Classifying rows"),
    aggregate: t("ui.calculate.progress.phase_aggregate",
                   "Aggregating results"),
    persist: t("ui.calculate.progress.phase_persist",
                 "Writing audit record"),
    complete: t("ui.calculate.progress.phase_complete", "Complete"),
    failed: t("ui.calculate.progress.phase_failed", "Failed"),
    cancelling: t("ui.calculate.progress.phase_cancelling", "Cancelling..."),
    cancelled: t("ui.calculate.progress.phase_cancelled", "Cancelled"),
  }[phase] || phase;

  const pct = rowsTotal > 0 ? Math.min(100, Math.round((rowsDone / rowsTotal) * 100)) : 0;
  const elapsedS = Math.floor(elapsedMs / 1000);

  return (
    <div data-testid="calc-progress-card"
         className="border border-teal-300 dark:border-teal-700 bg-white/90 dark:bg-slate-900/90 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="tabular-nums tracking-normal text-xs text-teal-700 dark:text-teal-300">
          {t("ui.calculate.progress.title", "Calculation in progress")}
        </div>
        <div data-testid="calc-progress-elapsed" className="tabular-nums text-xs text-slate-600 dark:text-slate-300">
          {elapsedS}s
        </div>
      </div>

      <div data-testid="calc-progress-phase" className="text-sm text-slate-800 dark:text-slate-100 tabular-nums">
        {phaseLabel}
      </div>

      {/* Rows progress */}
      <div>
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-slate-600 dark:text-slate-300">
            {t("ui.calculate.progress.rows_label", "Rows classified")}
          </span>
          <span data-testid="calc-progress-rows" className="text-teal-700 dark:text-teal-300">
            {rowsDone.toLocaleString()} / {rowsTotal.toLocaleString()}
          </span>
        </div>
        <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded overflow-hidden">
          <div data-testid="calc-progress-bar"
               style={{ width: `${pct}%` }}
               className="h-full bg-teal-500 transition-[width] duration-500" />
        </div>
      </div>

      {/* VIES progress */}
      {viesTotal > 0 && (
        <div className="flex items-center justify-between text-xs">
          <span className="text-slate-600 dark:text-slate-300">
            {t("ui.calculate.progress.vies_label", "VIES checks pending")}
          </span>
          <span data-testid="calc-progress-vies" className="text-amber-700 dark:text-amber-300">
            {viesPending.toLocaleString()} / {viesTotal.toLocaleString()}
          </span>
        </div>
      )}

      {/* Cancel — appears after 10s */}
      <div className="flex items-center justify-end pt-1">
        {showCancel && !confirmingCancel && (
          <button
            type="button"
            data-testid="calc-cancel-button"
            onClick={() => setConfirmingCancel(true)}
            className="text-xs text-red-700 dark:text-red-300 hover:text-red-200 border border-red-500/40 dark:border-red-800 hover:border-red-400 rounded-lg px-3 py-1.5 flex items-center gap-1.5"
          >
            <X size={12} weight="bold" />
            {t("ui.calculate.progress.cancel", "Cancel")}
          </button>
        )}
        {confirmingCancel && (
          <div className="flex items-center gap-2 tabular-nums text-xs">
            <span className="text-slate-500 dark:text-slate-400">
              {t("ui.calculate.progress.cancel_confirm",
                 "Cancel this calculation?")}
            </span>
            <button
              type="button"
              data-testid="calc-cancel-confirm-button"
              data-resolution-class="self-resolve"
              data-resolution-endpoint="navigation"
              onClick={() => { setConfirmingCancel(false); onCancel?.(); }}
              className="text-red-700 dark:text-red-300 hover:text-red-200 border border-red-500/60 rounded px-2 py-1 tracking-normal"
            >
              {t("ui.calculate.progress.cancel_yes", "Yes, cancel")}
            </button>
            <button
              type="button"
              data-testid="calc-cancel-abort-button"
              onClick={() => setConfirmingCancel(false)}
              className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-50 border border-slate-300 dark:border-slate-700 rounded px-2 py-1 tracking-normal"
            >
              {t("ui.calculate.progress.cancel_no", "Keep running")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Polling hook: kicks off `startFn` (returning {job_id}), then polls
 * `getFn(jobId)` every `intervalMs`. Returns the latest job doc and
 * stops on a terminal status.
 *
 * v0.4.39 T1 fix: handlers + job_id live in refs and a SINGLE mount-
 * time setInterval drives both polling and the elapsed-time tick.
 * Earlier impl declared the polling effect with handler deps, which
 * re-mounted the interval on every parent render (the elapsed-time
 * 500ms heartbeat caused parent re-renders, tearing down the 2s
 * polling interval before it ever ticked).
 */
export const useCalcJobPoll = ({
  startFn, getFn, resultFn, intervalMs = 2000, onComplete, onFailure,
  jobRegistry,
}) => {
  const [job, setJob] = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);

  // Stable refs so the single setInterval below never needs to be
  // torn down on parent re-renders.
  const jobRef = useRef(null);
  const startedAtRef = useRef(null);
  const handlersRef = useRef({});
  const resultFailsRef = useRef(0);   // A2-581 lead 5: result fetches failed in a row
  handlersRef.current = { getFn, resultFn, onComplete, onFailure, jobRegistry };
  useEffect(() => { jobRef.current = job; }, [job]);
  useEffect(() => { startedAtRef.current = startedAt; }, [startedAt]);

  // v0.6 — set the instant a new job starts (synchronously, not via the
  // `job` effect above which lags a render behind). A tick's two awaits
  // (getFn then resultFn) give a re-run's `start()` a window to land in
  // between; without this, a tick that captured the OLD job_id at its
  // start can resolve AFTER a newer job has already replaced it, merge
  // its stale data over the new job's state, and fire onComplete with
  // the old job's result — the mechanism behind the live "stale calc
  // signed off and frozen" bug. Every tick re-checks this ref after each
  // await and discards its result the instant it no longer matches.
  //
  // Local fallback only: a real workflow re-run can come from a
  // DIFFERENT mounted instance of this hook entirely (each workflow
  // step's CalculatePanel has its own). `jobRegistry` (from
  // WorkflowContext, spanning every step) is the real guard in
  // production — this local ref only covers same-instance races and
  // keeps the hook self-contained for callers with no shared registry.
  const currentJobIdRef = useRef(null);
  const registerJobStart = (jobId) => {
    resultFailsRef.current = 0;   // A2-581 lead 5: a new run starts its count afresh
    const reg = handlersRef.current.jobRegistry;
    if (reg) reg.register(jobId);
    else currentJobIdRef.current = jobId;
  };
  const isCurrentJob = (jobId) => {
    const reg = handlersRef.current.jobRegistry;
    return reg ? reg.isCurrent(jobId) : currentJobIdRef.current === jobId;
  };

  // ONE long-lived interval, mounted once, plus a visibility-change
  // listener. No deps on handlers → never recreated on parent re-renders.
  //
  // v0.6 — found live: the progress card could sit stuck ("Queued...",
  // elapsed climbing, zero status-check requests ever fired) for 80+
  // seconds on a backend job that had completed in under a second.
  // Root cause: this tick used to gate polling on a raw FIRING COUNT
  // (`tickCount % ticksPerPoll`) rather than elapsed wall-clock time.
  // Browser tabs a user has switched away from get their timers
  // throttled by the browser — sometimes down to roughly once a
  // minute — so `tickCount` can crawl upward far slower than real
  // time passes, and a check gated on "has this many FIRINGS
  // happened" inherits that same multiplied delay (up to
  // ticksPerPoll-1 throttled firings of extra wait) instead of
  // reacting the moment real time says a poll is overdue.
  //
  // Fixed two ways: (1) `pollDueRef` tracks wall-clock time since the
  // last poll attempt, so the very next tick that DOES fire — however
  // late — polls immediately if intervalMs has genuinely elapsed,
  // instead of waiting for more firings to accumulate; (2) a
  // `visibilitychange` listener fires an out-of-band poll the instant
  // the tab becomes visible again, rather than waiting on the
  // interval to resume its normal cadence on its own.
  useEffect(() => {
    let inFlight = false;
    const lastPollAtRef = { current: 0 };

    const pollNow = async () => {
      const cur = jobRef.current;
      if (!cur?.job_id) return;
      if (["COMPLETE", "FAILED", "CANCELLED"].includes(cur.status)) return;
      if (inFlight) return;
      inFlight = true;
      lastPollAtRef.current = Date.now();
      const pollingJobId = cur.job_id;
      try {
        const { getFn: gf, resultFn: rf, onComplete: oc, onFailure: of } =
          handlersRef.current;
        const fresh = await gf(pollingJobId);
        // A newer job superseded this one while the check was in
        // flight — discard, don't touch state or fire callbacks.
        if (!isCurrentJob(pollingJobId)) return;
        if (fresh.status === "COMPLETE") {
          // A2-581 screen map lead 5: the job was marked COMPLETE before
          // its result was fetched, and a failed fetch was swallowed, so
          // every later poll stopped at the COMPLETE check and the screen
          // hung on "Calculating...". The job counts as complete only once
          // its result is in; a result that keeps failing says so.
          let result;
          try {
            result = await rf(pollingJobId);
          } catch (e) {
            resultFailsRef.current += 1;
            if (resultFailsRef.current >= RESULT_TRIES && isCurrentJob(pollingJobId)) {
              // review of fe438e76: the job ends FAILED, so the card does not
              // show the server's "Complete" beside the failure message
              setJob((prev) => ({ ...(prev || {}), ...fresh, status: "FAILED", error: RESULT_UNAVAILABLE }));
              of?.(RESULT_UNAVAILABLE);
            }
            return;   // job state unchanged: the next poll asks again
          }
          if (!isCurrentJob(pollingJobId)) return;
          resultFailsRef.current = 0;
          setJob((prev) => ({ ...(prev || {}), ...fresh }));
          oc?.(result);
        } else {
          setJob((prev) => ({ ...(prev || {}), ...fresh }));
          if (fresh.status === "FAILED") of?.(fresh.error);
        }
      } catch (e) { /* swallow transient poll errors */ }
      finally { inFlight = false; }
    };

    const id = setInterval(() => {
      const sa = startedAtRef.current;
      if (sa) setElapsedMs(Date.now() - sa);
      if (Date.now() - lastPollAtRef.current >= intervalMs) pollNow();
    }, 500);

    const onVisible = () => {
      if (document.hidden) return;
      pollNow();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [intervalMs]);

  const start = async (payload) => {
    const res = await startFn(payload);
    registerJobStart(res.job_id);
    setJob({ job_id: res.job_id, status: res.status, progress: {} });
    setStartedAt(Date.now());
    setElapsedMs(0);
    return res;
  };

  // Local reset only — does NOT touch the shared job registry. This
  // instance giving up on its own view of a job must not invalidate a
  // legitimately newer job some OTHER instance has since registered.
  const reset = () => { setJob(null); setStartedAt(null); setElapsedMs(0); };

  return { job, elapsedMs, start, reset };
};
